import {
  Injectable,
  OnDestroy,
  Optional,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  NEVER,
  Observable,
  Subject,
  Subscription,
  asapScheduler,
  catchError,
  concatMap,
  defer,
  delay,
  filter,
  finalize,
  from,
  fromEvent,
  map,
  mapTo,
  merge,
  observeOn,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
  timer,
} from 'rxjs';

import {
  navigationForceSubNavigationOpen,
  navigationSetIsOpen,
} from '../navigation/navigation-state/navigation-state.actions';
import { ZalamoTooltipService } from '../zalamo-tooltip/zalamo-tooltip.service';
import { FullscreenService } from '../fullscreen/fullscreen.service';
import { ZalamoDialogService } from '../zalamo-dialog/zalamo-dialog.service';
import {
  getVisualGuideActions,
  VisualGuideDialog,
  VisualGuideEvents,
  VisualGuideStep,
  VisualGuideSteps,
} from './types';
import {
  clearGuideMessage,
  clearGuideOverlay,
  hideMessage,
  hideOverlay,
  openHover,
  setGuideClickEnabled,
  showMessage,
} from './helpers';
import {
  GetMineVisualGuidesStateDocument,
  MutationUpdateVisualGuideStateArgs,
  UpdateVisualGuideStateGQL,
} from './graphql';

interface StepRequest {
  index: number;
  generation: number;
  backward: boolean;
}
export interface VisualGuideIssue {
  visualGuideId?: string;
  visualGuideStepId?: string;
  reason: 'missing-target' | 'playback-error' | 'state-save-error';
}

const isVisible = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  return element.isConnected && rect.width > 0 && rect.height > 0;
};

@Injectable({ providedIn: 'root' })
export class VisualGuideService implements OnDestroy {
  guideInProgress = false;
  allowInterception = false;
  isHoverOpen = false;
  isClickEnabled = false;
  currentVisualGuideId: string | null = null;
  readonly stateChanges$ = new Subject<void>();
  readonly issues$ = new Subject<VisualGuideIssue>();

  private pendingStateUpdates = 0;
  get isSavingState(): boolean {
    return this.pendingStateUpdates > 0;
  }

  private currentGuide: VisualGuideSteps = [];
  private currentStep = 0;
  private currentDialog: VisualGuideDialog | null = null;
  private generation = 0;
  private otherGuidesLoaded = false;
  private readonly events$ = new Subject<VisualGuideEvents>();
  private readonly stepRequests$ = new Subject<StepRequest>();
  private readonly breakMove$ = new Subject<void>();
  private readonly guideClosed$ = new Subject<void>();
  private readonly stateUpdates$ =
    new Subject<MutationUpdateVisualGuideStateArgs>();
  private readonly lifetime = new Subscription();
  private readonly startedGuides = new Set<string>();
  private readonly finishedGuides = new Set<string>();
  private allGuides: Record<string, VisualGuideSteps> = {};
  private guideNodes = new Map<HTMLElement, string>();
  private guideNodesRegistered = false;
  private manualGuideNodes = new Set<HTMLElement>();

  get currentStepDetails(): VisualGuideStep | undefined {
    return this.currentGuide[this.currentStep];
  }

  get nextStepFromCurrentGuide(): VisualGuideStep | undefined {
    const id = this.currentStepDetails?.visualGuideId;
    return this.currentGuide.find(
      (step, index) => index > this.currentStep && step.visualGuideId === id,
    );
  }

  constructor(
    private readonly store: Store,
    _tooltipService: ZalamoTooltipService,
    private readonly fullscreenService: FullscreenService,
    private readonly dialogs: ZalamoDialogService,
    private readonly updateStateGQL: UpdateVisualGuideStateGQL,
    @Optional() private readonly sanitizer?: DomSanitizer,
  ) {
    this.lifetime.add(
      this.stepRequests$
        .pipe(
          // Auto-advancement may emit while a synchronous step is finishing.
          observeOn(asapScheduler),
          switchMap((request) =>
            this.isCurrent(request)
              ? this.playStep(request).pipe(
                  takeUntil(this.breakMove$),
                  catchError(() => {
                    if (this.isCurrent(request))
                      this.showUnavailableStep('playback-error');
                    return EMPTY;
                  }),
                )
              : EMPTY,
          ),
        )
        .subscribe(),
    );
    this.lifetime.add(
      this.stateUpdates$
        .pipe(
          concatMap((update) =>
            defer(() =>
              this.updateStateGQL.mutate({
                ...{
                  refetchQueries: [{ query: GetMineVisualGuidesStateDocument }],
                  awaitRefetchQueries: true,
                },
                variables: update,
              }),
            ).pipe(
              take(1),
              catchError(() => {
                this.issues$.next({
                  visualGuideId: update.visualGuideId,
                  reason: 'state-save-error',
                });
                return EMPTY;
              }),
              finalize(() => {
                this.pendingStateUpdates--;
                this.stateChanges$.next(undefined);
              }),
            ),
          ),
        )
        .subscribe(),
    );
    this.lifetime.add(
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(
          filter(() => this.guideInProgress),
          filter((event) => !event.altKey && !event.ctrlKey && !event.metaKey),
          filter(
            (event) =>
              !(
                event.target instanceof Element &&
                event.target.closest(
                  'input, textarea, select, [contenteditable="true"]',
                )
              ),
          ),
          tap((event) => {
            if (event.key === 'Escape') this.closeTour();
            if (event.key === 'ArrowRight') this.goForward();
            if (event.key === 'ArrowLeft') this.goBackward();
          }),
        )
        .subscribe(),
    );
    this.lifetime.add(
      this.dialogs.watchIfAnythingOpen().subscribe((dialog) => {
        this.currentDialog = dialog;
      }),
    );
  }

  ngOnDestroy(): void {
    this.closeTour();
    this.lifetime.unsubscribe();
    this.events$.complete();
    this.stepRequests$.complete();
    this.breakMove$.complete();
    this.guideClosed$.complete();
    this.stateUpdates$.complete();
    this.stateChanges$.complete();
    this.issues$.complete();
    this.guideNodes.clear();
    this.manualGuideNodes.clear();
  }

  watchEvents(): Observable<VisualGuideEvents> {
    return this.events$.asObservable();
  }
  takeEvent(event: VisualGuideEvents): void {
    this.events$.next(event);
  }
  setAllGuides(guides: Record<string, VisualGuideSteps>): void {
    this.allGuides = guides;
  }

  setGuideNodes(
    nodes: ReadonlyMap<HTMLElement, string>,
    excludedNodes: ReadonlySet<HTMLElement> = new Set(),
  ): void {
    this.guideNodesRegistered = true;
    this.guideNodes = new Map(nodes);
    this.manualGuideNodes = new Set(excludedNodes);
  }

  start(
    elements: VisualGuideSteps,
    forced = false,
    fromSubGuide = false,
  ): void {
    if (!elements.length) return;
    if (this.guideInProgress && forced && !fromSubGuide) this.closeTour();
    if (this.guideInProgress) {
      if (!this.allowInterception) return;
      this.insertSubGuides(elements);
      return;
    }
    this.startedGuides.clear();
    this.finishedGuides.clear();
    this.otherGuidesLoaded = false;
    this.currentGuide = elements.map((step) => ({
      ...step,
      currentDialogWindow: this.currentDialog,
    }));
    this.guideInProgress = true;
    this.goToStep(0);
  }

  goForward(_fromLoadedActions = false): void {
    if (!this.guideInProgress) return;
    if (
      this.currentStepDetails?.forceWaitingForOtherGuides &&
      !this.otherGuidesLoaded
    )
      return;
    if (this.currentStep + 1 < this.currentGuide.length) {
      this.goToStep(this.currentStep + 1);
    } else {
      this.finishGuide(this.currentStepDetails?.visualGuideId);
      this.closeTour();
    }
  }

  goBackward(_force = false): void {
    if (!this.guideInProgress) return;
    for (let index = this.currentStep - 1; index >= 0; index--) {
      const step = this.currentGuide[index];
      if (
        !step.isEnd &&
        !step.waitForOtherGuides &&
        !step.pauseOnActions?.length
      ) {
        this.goToStep(index, true, true);
        return;
      }
    }
  }

  goToStep(index: number, _force = false, backward = false): void {
    if (!this.guideInProgress || !this.currentGuide[index]) return;
    this.generation++;
    this.breakMove$.next(undefined);
    this.currentStep = index;
    const step = this.currentGuide[index];
    const next = this.currentGuide[index + 1];
    this.allowInterception = !!(
      step.waitForOtherGuides || next?.waitForOtherGuides
    );
    this.otherGuidesLoaded = !!(
      step.waitForOtherGuides &&
      next &&
      next.visualGuideId !== step.visualGuideId
    );
    this.stepRequests$.next({ index, generation: this.generation, backward });
    this.stateChanges$.next(undefined);
  }

  closeTour(): void {
    this.generation++;
    this.breakMove$.next(undefined);
    this.guideClosed$.next(undefined);
    clearGuideMessage();
    clearGuideOverlay();
    this.currentGuide = [];
    this.currentStep = 0;
    this.currentVisualGuideId = null;
    this.guideInProgress = false;
    this.allowInterception = false;
    this.otherGuidesLoaded = false;
    this.isClickEnabled = false;
    this.isHoverOpen = false;
    this.startedGuides.clear();
    this.finishedGuides.clear();
    this.stateChanges$.next(undefined);
  }

  hideMessage(): Promise<void> {
    return hideMessage();
  }
  hideOverlay(): Promise<void> {
    this.isHoverOpen = false;
    return hideOverlay();
  }
  enableClick(): void {
    this.isClickEnabled = true;
    setGuideClickEnabled(true);
  }
  disableClick(): void {
    this.isClickEnabled = false;
    setGuideClickEnabled(false);
  }

  goToElement(element: HTMLElement): Promise<void> {
    const ready = openHover(element, 5, this.isClickEnabled);
    this.isHoverOpen = true;
    return ready;
  }

  showMessage(
    structure: Array<{ type: 'text'; value: string }>,
    anchor: HTMLElement,
    navigation: { hasPrevStep: boolean; hasNextStep: boolean },
  ): Promise<void> {
    const content = structure.map((part) => ({
      ...part,
      value: this.sanitizer
        ? this.sanitizer.sanitize(SecurityContext.HTML, part.value) || ''
        : this.escapeText(part.value),
    }));
    return showMessage(this.dialogs, content, anchor, {
      ...navigation,
      onClose: () => this.closeTour(),
      onPrevious: () => this.goBackward(),
      onNext: () => this.goForward(),
    });
  }

  removeSubGuide(visualGuideId: string, node?: HTMLElement): void {
    const active = this.currentStepDetails;
    const before = this.currentGuide;
    const belongs = (step: VisualGuideStep) =>
      step.visualGuideId === visualGuideId && (!node || step.node === node);
    if (!before.some(belongs)) return;
    this.currentGuide = before.filter((step) => !belongs(step));
    this.otherGuidesLoaded = false;
    if (!this.currentGuide.length) {
      this.closeTour();
      return;
    }
    const remainingIndex = this.currentGuide.indexOf(active);
    if (remainingIndex >= 0) {
      this.currentStep = remainingIndex;
      // The request index may have changed even though its logical step survived.
      this.goToStep(remainingIndex);
    } else {
      const precedingCount = before
        .slice(0, this.currentStep)
        .filter((step) => !belongs(step)).length;
      this.goToStep(Math.min(precedingCount, this.currentGuide.length - 1));
    }
  }

  private isCurrent(request: StepRequest): boolean {
    return this.guideInProgress && request.generation === this.generation;
  }

  private playStep(request: StepRequest): Observable<void> {
    return defer(() => {
      const step = this.currentGuide[request.index];
      if (!step) return EMPTY;
      if (
        step.currentDialogWindow !== this.currentDialog &&
        this.currentDialog
      ) {
        const closing = this.currentDialog;
        this.currentDialog = step.currentDialogWindow || null;
        closing.close();
        this.currentGuide.forEach((item) => {
          if (item.currentDialogWindow === closing)
            item.currentDialogWindow = null;
        });
      }
      this.beginGuide(step);
      if (step.isEnd) {
        this.finishGuide(step.visualGuideId);
        this.goForward();
        return EMPTY;
      }
      if (step.waitForOtherGuides) {
        this.enableClick();
        this.collectSubGuides(request.index);
        if (
          this.isCurrent(request) &&
          this.otherGuidesLoaded &&
          step.goToNextStepWhenOtherGuidesLoaded
        )
          this.goForward(true);
        return NEVER;
      }
      if (step.pauseOnActions?.length) return this.waitForResume(step);
      const actions = getVisualGuideActions(step);
      const actionReady = actions.length
        ? this.events$.pipe(
            filter((event) => actions.includes(event)),
            take(1),
            delay(Math.max(0, step.actionDelay || 0)),
            mapTo(undefined),
          )
        : of(undefined);
      return actionReady.pipe(concatMap(() => this.displayStep(step, request)));
    });
  }

  private displayStep(
    step: VisualGuideStep,
    request: StepRequest,
  ): Observable<void> {
    this.store.dispatch(navigationSetIsOpen({ isOpen: !!step.openMainMenu }));
    if (step.isSubNav && localStorage.subNavToggled === 'true')
      this.store.dispatch(navigationForceSubNavigationOpen());
    if (step.hideOverlay) {
      return from(this.hideOverlay()).pipe(
        tap(() => {
          if (this.isCurrent(request)) this.goForward();
        }),
      );
    }
    if (step.fullscreenMessage) {
      this.fullscreenService.open({ content: step.fullscreenMessage });
      return EMPTY;
    }
    if (!step.target && !step.waitForMenuSelect) {
      if (getVisualGuideActions(step).length) {
        this.goForward();
      } else {
        this.showUnavailableStep('missing-target');
      }
      return EMPTY;
    }
    const waitMs = request.backward
      ? Math.max(250, step.waitTimeoutOnGoingBack || 10000)
      : 10000;
    return timer(0, 100).pipe(
      map(() => this.findTarget(step)),
      filter((element): element is HTMLElement => !!element),
      take(1),
      timeout(waitMs),
      catchError(() => {
        if (this.isCurrent(request)) this.showUnavailableStep('missing-target');
        return EMPTY;
      }),
      concatMap((element) => {
        clearGuideMessage();
        if (
          step.enableClick ||
          step.waitForMenuSelect ||
          this.allowInterception
        )
          this.enableClick();
        else this.disableClick();
        return from(this.goToElement(element)).pipe(
          filter(() => this.isCurrent(request) && element.isConnected),
          concatMap(() =>
            step.message
              ? from(
                  this.showMessage(
                    [{ type: 'text', value: step.message }],
                    element,
                    { hasPrevStep: this.currentStep > 0, hasNextStep: true },
                  ),
                )
              : of(undefined),
          ),
          tap(() => {
            if (!this.isCurrent(request)) return;
            if (this.allowInterception)
              this.collectSubGuides(request.index + 1);
            if (this.isCurrent(request) && !step.manualAck) this.goForward();
          }),
        );
      }),
    );
  }

  private findTarget(
    step: VisualGuideStep,
    visible = true,
  ): HTMLElement | undefined {
    if (step.waitForMenuSelect) {
      return Array.from(
        document.querySelectorAll<HTMLElement>('.mat-select-panel'),
      ).find(isVisible);
    }
    const targets = Array.from(
      document.querySelectorAll<HTMLElement>('[data-vg]'),
    ).filter(
      (element) =>
        element.getAttribute('data-vg') === step.target &&
        (!visible || isVisible(element)),
    );
    return (
      targets.find((element) => step.node?.contains(element)) || targets[0]
    );
  }

  private queueStateUpdate(update: MutationUpdateVisualGuideStateArgs): void {
    this.pendingStateUpdates++;
    this.stateUpdates$.next(update);
  }

  private beginGuide(step: VisualGuideStep): void {
    const id = step.visualGuideId;
    this.currentVisualGuideId = id || null;
    if (!id) return;
    const firstVisit = !this.startedGuides.has(id);
    this.startedGuides.add(id);
    this.queueStateUpdate({
      visualGuideId: id,
      update: {
        ...(firstVisit ? { started: true } : {}),
        lastStep: step.visualGuideStepId,
      },
    });
  }

  private finishGuide(id?: string): void {
    if (!id || this.finishedGuides.has(id)) return;
    this.finishedGuides.add(id);
    this.queueStateUpdate({ visualGuideId: id, update: { finished: true } });
  }

  private waitForResume(step: VisualGuideStep): Observable<void> {
    return this.events$.pipe(
      takeUntil(merge(this.breakMove$, this.guideClosed$)),
      tap((event) => {
        if (step.pauseOnActions?.includes(event)) {
          clearGuideMessage();
          clearGuideOverlay();
          this.isHoverOpen = false;
        }
        if (step.resumeOnActions?.includes(event)) {
          const next = this.currentGuide.findIndex(
            (candidate, index) =>
              index > this.currentStep &&
              !candidate.isEnd &&
              !candidate.waitForOtherGuides &&
              !candidate.pauseOnActions?.length,
          );
          if (next >= 0) this.goToStep(next);
          else this.closeTour();
        }
      }),
      mapTo(undefined),
    );
  }

  private insertSubGuides(elements: VisualGuideSteps): void {
    const current = this.currentStepDetails;
    const waitingIndex = current?.waitForOtherGuides
      ? this.currentStep
      : this.currentStep + 1;
    const waiting = this.currentGuide[waitingIndex];
    if (!waiting?.waitForOtherGuides) return;
    const sameStep = (left: VisualGuideStep, right: VisualGuideStep) =>
      left.visualGuideId === right.visualGuideId &&
      left.visualGuideStepId === right.visualGuideStepId &&
      left.node === right.node;
    const additions: VisualGuideSteps = [];
    for (const step of elements) {
      if (
        (step.visualGuideId === waiting.visualGuideId &&
          step.node === waiting.node) ||
        additions.some((existing) => sameStep(existing, step))
      )
        continue;
      const existingIndex = this.currentGuide.findIndex((existing) =>
        sameStep(existing, step),
      );
      // Already visited steps must not be replayed. A queued child belongs at
      // this wait boundary, even when start() received several guides at once.
      if (existingIndex >= 0 && existingIndex <= waitingIndex) continue;
      additions.push(
        existingIndex >= 0
          ? this.currentGuide[existingIndex]
          : { ...step, currentDialogWindow: this.currentDialog },
      );
    }
    if (!additions.length) return;
    this.currentGuide = this.currentGuide.filter(
      (step, index) =>
        index <= waitingIndex ||
        !additions.some((addition) => sameStep(addition, step)),
    );
    this.currentGuide.splice(waitingIndex + 1, 0, ...additions);
    this.otherGuidesLoaded = true;
    if (waiting.goToNextStepWhenOtherGuidesLoaded) {
      this.goToStep(waitingIndex + 1);
    } else {
      this.stateChanges$.next(undefined);
    }
  }

  canAcceptSubGuide(
    node: HTMLElement,
    registeredNodes?: ReadonlyArray<HTMLElement>,
  ): boolean {
    const waitingIndex = this.currentStepDetails?.waitForOtherGuides
      ? this.currentStep
      : this.currentStep + 1;
    return this.isInSubGuideRange(node, waitingIndex, registeredNodes);
  }

  private isInSubGuideRange(
    node: HTMLElement,
    waitingIndex: number,
    registeredNodes?: ReadonlyArray<HTMLElement>,
  ): boolean {
    const waiting = this.currentGuide[waitingIndex];
    if (
      !waiting?.waitForOtherGuides ||
      this.manualGuideNodes.has(node) ||
      !isVisible(node) ||
      node === waiting.node
    )
      return false;
    const belongsToParent = (step: VisualGuideStep) =>
      step.visualGuideId === waiting.visualGuideId &&
      step.node === waiting.node;
    const previous = this.currentGuide
      .slice(0, waitingIndex)
      .reverse()
      .find((step) => !!step.target && belongsToParent(step));
    const following = this.currentGuide
      .slice(waitingIndex + 1)
      .find(
        (step) =>
          !!step.target &&
          belongsToParent(step) &&
          step.target !== previous?.target,
      );
    const start = previous && this.findTarget(previous);
    const end = following && this.findTarget(following, false);
    if (!start || !start.compareDocumentPosition(node)) return false;
    const registrations = registeredNodes || [...this.getGuideNodes().keys()];
    const ancestors = waiting.node
      ? [...registrations, waiting.node]
      : registrations;
    const nearestGuideAncestor = (element: HTMLElement) =>
      ancestors
        .filter(
          (candidate) => candidate !== element && candidate.contains(element),
        )
        .reduce<HTMLElement | undefined>(
          (nearest, candidate) =>
            !nearest || nearest.contains(candidate) ? candidate : nearest,
          undefined,
        );
    // Some guides intentionally leave their initial row (e.g. file prices).
    // Follow the actual target's enclosing guide, retaining section boundaries.
    const scope = waiting.node?.contains(start)
      ? waiting.node
      : nearestGuideAncestor(start) || start.parentElement;
    const parentGuide = nearestGuideAncestor(node);
    if (
      !scope?.contains(node) ||
      node === scope ||
      (parentGuide && parentGuide !== scope)
    )
      return false;
    const isAfterStart =
      start.contains(node) ||
      !!(
        start.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING
      );
    const isBeforeEnd =
      !end ||
      node.contains(end) ||
      !!(node.compareDocumentPosition(end) & Node.DOCUMENT_POSITION_FOLLOWING);
    return isAfterStart && isBeforeEnd;
  }

  private getGuideNodes(): Map<HTMLElement, string> {
    // Manual registrations remain part of scope detection, but cannot be
    // candidates for automatic collection in isInSubGuideRange.
    const nodes = new Map(this.guideNodes);
    // Once connected, the manager owns the complete registry, including
    // explicit scope overrides and removals. DOM discovery is only a legacy
    // fallback for a runtime without a manager.
    if (!this.guideNodesRegistered)
      document
        .querySelectorAll<HTMLElement>('[libVisualGuideInitiator]')
        .forEach((node) =>
          nodes.set(node, node.getAttribute('libVisualGuideInitiator') || ''),
        );
    for (const [node, container] of nodes)
      if (!node.isConnected || !this.allGuides[container]?.length)
        nodes.delete(node);
    return nodes;
  }

  private collectSubGuides(waitingIndex: number): void {
    const elements: VisualGuideSteps = [];
    const nodes = this.getGuideNodes();
    const registrations = [...nodes.keys()];
    for (const [node, container] of nodes) {
      if (!this.isInSubGuideRange(node, waitingIndex, registrations)) continue;
      elements.push(
        ...this.allGuides[container].map((step) => ({ ...step, node })),
      );
    }
    if (elements.length) this.insertSubGuides(elements);
  }

  private showUnavailableStep(reason: VisualGuideIssue['reason']): void {
    const step = this.currentStepDetails;
    this.issues$.next({
      visualGuideId: step?.visualGuideId,
      visualGuideStepId: step?.visualGuideStepId,
      reason,
    });
    clearGuideOverlay();
    this.isHoverOpen = false;
    this.enableClick();
    void this.showMessage(
      [
        {
          type: 'text',
          value:
            'Ten krok jest teraz niedostępny. Możesz przejść dalej lub zakończyć przewodnik.',
        },
      ],
      document.documentElement,
      { hasPrevStep: this.currentStep > 0, hasNextStep: true },
    );
  }

  private escapeText(text: string): string {
    const element = document.createElement('div');
    element.textContent = text;
    return element.innerHTML;
  }
}
